import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faCheckCircle, faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { primaryColor } from "../../../../../../utils/comon";
import apiServices from "../../../../../../services/apiService";

const UnitSelection = ({ formData, onUpdate, onNext, onBack, nextLabel = "Generate Pass" }) => {
  const [search, setSearch] = useState("");
  const primary = primaryColor();
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const pressTimer = useRef(null);
  const isLongPress = useRef(false);

  const handlePressStart = (unit) => {
    isLongPress.current = false;
    pressTimer.current = setTimeout(() => {
      isLongPress.current = true;
      setIsMultiSelectMode(true);
      // For long press, if not already selected, select it.
      const isAlreadySelected = formData.selectedUnits?.some(u => (u.UnitId || u.unitId || u.id) === (unit.UnitId || unit.unitId || unit.id));
      if (!isAlreadySelected) {
        onUpdate({ selectedUnits: [...(formData.selectedUnits || []), unit] });
      }
    }, 500);
  };

  const handlePressEnd = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };

  const handleClick = (unit) => {
    if (isLongPress.current) return;
    
    if (isMultiSelectMode) {
      const isAlreadySelected = formData.selectedUnits?.some(u => (u.UnitId || u.unitId || u.id) === (unit.UnitId || unit.unitId || unit.id));
      let newSelection = formData.selectedUnits || [];
      if (isAlreadySelected) {
        newSelection = newSelection.filter(u => (u.UnitId || u.unitId || u.id) !== (unit.UnitId || unit.unitId || unit.id));
        if (newSelection.length === 0) setIsMultiSelectMode(false);
      } else {
        newSelection = [...newSelection, unit];
      }
      onUpdate({ selectedUnits: newSelection });
    } else {
      onUpdate({ selectedUnits: [unit] });
    }
  };

  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const response = await apiServices({
          method: "GET",
          url: `/UnitMaster/GetAllUnitMaster?pageNumber=1&pageSize=1000`
        });
        if (response?.success || response?.isSuccess) {
          const data = response.data?.data || response.data || [];
          setUnits(data);
        }
      } catch (err) {
        console.error("Error fetching units:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchUnits();
  }, []);

  const filteredUnits = units.filter(u => {
    const unitNum = u.UnitNumber || u.unitNumber || "";
    const tName = u.UnitName || u.unitName || "";
    return unitNum.toLowerCase().includes(search.toLowerCase()) ||
      tName.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="flex-1 flex flex-col h-full bg-[#F6F6F4]">
      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 pt-8 pb-4">
        {/* Header */}
        <p className="text-2xl font-black tracking-tight leading-none text-center cursor-pointer"
          style={{ color: primary }}
          onClick={() => window.open("https://myhello.in/", "_blank")}
        >
          Hello!
        </p>
        <h1 className="text-sm font-black text-[#191C1D] mt-4 leading-tight">Select Unit</h1>

        {/* Custom Stepper Dots */}
        <div className="flex items-center gap-1.5 mt-2 mb-6">
          <div className="w-1 h-1 rounded-full bg-gray-200" />
          <div className="w-4 h-1 rounded-full" style={{ backgroundColor: primary }} />
        </div>

        {/* Search Bar */}
        <div className="relative mb-4">
          <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input
            type="text"
            placeholder="Search by unit or company..."
            className="w-full bg-white border border-gray-100 rounded-lg py-2 pl-9 pr-4 text-xs font-medium outline-none focus:ring-2 focus:ring-offset-1 transition-all shadow-sm placeholder:font-semibold"
            style={{ focusRing: primary }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-10">
            <div className="w-8 h-8 border-4 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {filteredUnits.map((unit) => {
              const uId = unit.UnitId || unit.unitId || unit.id;
              const isSelected = formData.selectedUnits && formData.selectedUnits.some(u => (u.UnitId || u.unitId || u.id) === uId);
              return (
                <div
                  key={uId}
                  onPointerDown={() => handlePressStart(unit)}
                  onPointerUp={() => { handlePressEnd(); handleClick(unit); }}
                  onPointerLeave={handlePressEnd}
                  onContextMenu={(e) => e.preventDefault()}
                  className={`relative p-3 rounded-lg cursor-pointer transition-all border-2 ${isSelected ? "bg-white shadow-lg" : "bg-[#F8FAFC] border-transparent hover:border-gray-200"
                    }`}
                  style={{ borderColor: isSelected ? primary : undefined, userSelect: 'none', WebkitUserSelect: 'none' }}
                >
                  {isSelected && (
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className="absolute top-2 right-2 text-sm"
                      style={{ color: primary }}
                    />
                  )}
                  <p className="text-base font-black text-[#191C1D] tracking-tight" style={{ color: isSelected ? primary : "#191C1D" }}>
                    {unit.UnitNumber || unit.unitNumber}
                  </p>
                  <p className="text-[10px] font-bold text-gray-500 mt-1 leading-tight">
                    {unit.UnitName || unit.unitName}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer Buttons */}
      <div className="px-2 py-3 bg-white flex gap-3 border-t border-gray-50">
        <button
          onClick={onBack}
          className="flex-1 py-2 rounded-lg bg-white text-xs font-black flex items-center justify-center gap-2 active:scale-95 transition-all shadow-sm"
          style={{ border: `2px solid ${primary}`, color: primary }}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back
        </button>

        <button
          onClick={onNext}
          disabled={!formData.selectedUnits || formData.selectedUnits.length === 0}
          className="flex-[2] py-2 rounded-lg text-white text-sm font-black flex items-center justify-center gap-2 disabled:opacity-50 active:scale-95 transition-all shadow-lg"
          style={{ backgroundColor: primary }}
        >
          {nextLabel} <FontAwesomeIcon icon={faArrowRight} />
        </button>
      </div>
    </div>
  );
};

export default UnitSelection;
